import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../../api/client";
import { GlobalContext } from "../../../context";
import { fmtDay } from "../home/time";
import template from "./cert-template.jpg";
import Icon from "./Icon";
import PageFrame, { LoadFailed, Loading } from "./PageFrame";
import { HowGraded, ReqChips } from "./GradeBreakdown";
import { DEFAULT_CRITERIA, summarize } from "./progress";
import useCoursePlans from "./useCoursePlans";

const BACKEND = process.env.REACT_APP_DOMAIN_URL || "";
const verifyUrl = (uuid) => `${BACKEND}/verify-certificate/${uuid}`;
const monthYear = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" }).replace(" ", ", ").toUpperCase() : "");

/**
 * The real certificate layout (cert-template.jpg, a blank copy of the issued PDF) with the student's
 * own name, course and date laid over it, so the card looks like the certificate they'll get.
 */
function CertPreview({ name, course, date, number }) {
  // Drawn in the PDF's own coordinates (842 x 595 pt), so it scales with the image. Long text is
  // squeezed to fit the left panel, as the PDF does, instead of running into the seal.
  const fit = (text, perChar, max) => ((text || "").length * perChar > max ? { textLength: max, lengthAdjust: "spacingAndGlyphs" } : {});
  const title = (course || "").toUpperCase();
  return (
    <div className="cert-paper">
      <img src={template} alt="" />
      <svg viewBox="0 0 842 595" aria-hidden="true">
        <text x="60" y="360" className="cp-name" {...fit(name, 19, 480)}>{name}</text>
        <text x="60" y="468" className="cp-course" {...fit(title, 22, 480)}>{title}</text>
        {date && <text x="686" y="427" className="cp-date">{date}</text>}
        {number && <text x="60" y="580" className="cp-no">Certificate No: {number}</text>}
      </svg>
    </div>
  );
}

function Issued({ cert, name, onView }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND}/api/certificates/${cert.uuid}/download`, { headers: { Authorization: client.defaults.headers.Authorization || "" } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `E1X_Certificate_${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("We couldn't download your certificate. Please try again.");
    }
    setDownloading(false);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl(cert.uuid));
      toast.success("Verification link copied");
    } catch {
      toast.info(verifyUrl(cert.uuid));
    }
  };
  const issued = cert.issue_date ? new Date(cert.issue_date) : null;
  const linkedIn = `https://www.linkedin.com/profile/add?${new URLSearchParams({
    startTask: "CERTIFICATION_NAME", name: cert.course?.title || "E1X Academy certificate", organizationName: "E1X Academy",
    ...(issued ? { issueYear: issued.getFullYear(), issueMonth: issued.getMonth() + 1 } : {}),
    certUrl: verifyUrl(cert.uuid), certId: cert.certificate_number,
  })}`;

  return (
    <article className="cert">
      <button className="cert-img" type="button" onClick={onView} aria-label="View certificate full size">
        <CertPreview name={name} course={cert.course?.title} date={monthYear(cert.issue_date)} number={cert.certificate_number} />
      </button>
      <div className="cert-body">
        <h4>{cert.course?.title}</h4>
        <span className="sub">{cert.cohort?.name}{issued ? ` · issued ${fmtDay(cert.issue_date)}` : ""}</span>
        <div className="act">
          <button className="cbtn go" type="button" onClick={download} disabled={downloading}>{downloading ? "Downloading…" : "Download PDF"}</button>
          <button className="cbtn quiet" type="button" onClick={onView}>View</button>
        </div>
        <div className="share">
          <button className="link-btn" type="button" onClick={copy}><Icon name="copy" size={14} /> Copy verification link</button>
          <a className="link-btn" href={linkedIn} target="_blank" rel="noopener noreferrer"><Icon name="link" size={14} /> Add to LinkedIn</a>
        </div>
      </div>
    </article>
  );
}

function NotYet({ course, plan, name, onGrades }) {
  const pending = course.status !== "active";
  const g = summarize(plan);
  return (
    <article className={`cert ${pending ? "pending" : "locked"}`}>
      <div className="cert-img dim">
        <CertPreview name={name} course={course.course.title} />
        <span><Icon name={pending ? "clock" : "lock"} size={14} /> {pending ? "Certificate in Progress" : "Not Earned Yet"}</span>
      </div>
      <div className="cert-body">
        <h4>{course.course.title}</h4>
        <span className="sub">{course.cohort.name} · {pending ? (course.ended_on ? `classes ended ${fmtDay(course.ended_on)}` : "classes ended") : "in progress"}</span>
        {pending && <div className="cert-note"><Icon name="clock" size={14} /><span>Your certificate is issued once you meet every requirement and your capstone is marked. We'll email it to you.</span></div>}
        <ReqChips g={g} />
        <button className="link-btn" type="button" onClick={onGrades}>See what's behind each score in Grades ›</button>
      </div>
    </article>
  );
}

/** Certificates (prototype certsPage): issued certificates, plus where each other course stands. */
export default function CertificatesPage() {
  const navigate = useNavigate();
  const { authState: { data } } = useContext(GlobalContext);
  const user = data?.user;
  const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ");
  const { plans, failed, reload } = useCoursePlans();
  const [certs, setCerts] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    client.get("/certificates").then((r) => setCerts(r.data?.data || [])).catch(() => setCerts(false));
  }, []);

  const intro = "A certificate is issued when you meet every requirement for the course: attendance, activities and the capstone project.";
  if (failed || certs === false) return <PageFrame title="Certificates" kicker="Career" intro={intro}><LoadFailed what="certificates" onRetry={() => { reload(); window.location.reload(); }} /></PageFrame>;
  if (!plans || !certs) return <PageFrame title="Certificates" kicker="Career" intro={intro}><Loading /></PageFrame>;

  const certified = new Set(certs.map((c) => c.course?.uuid));
  const others = plans.filter((c) => !certified.has(c.course.uuid) && c.status !== "completed");
  const viewing = open && certs.find((c) => c.uuid === open);

  return (
    <PageFrame title="Certificates" kicker="Career" intro={intro}>
      <div className="notice">
        <Icon name="info" size={18} />
        <div>
          <strong>Check your name before your certificate is issued</strong>
          <span>Your certificate shows your name exactly as it is in your profile: <b>{name || "—"}</b>. If it's misspelled or in the wrong order, correct it before your capstone is marked.</span>
          <button className="link-btn" type="button" onClick={() => navigate("/dashboard/settings")}>Edit your name in Settings ›</button>
        </div>
      </div>

      <HowGraded criteria={plans[0]?.plan.criteria || DEFAULT_CRITERIA} />

      {certs.length || others.length ? (
        <div className="cert-grid">
          {certs.map((c) => <Issued key={c.uuid} cert={c} name={name} onView={() => setOpen(c.uuid)} />)}
          {others.map((c) => <NotYet key={c.course.uuid} course={c} plan={c.plan} name={name} onGrades={() => navigate("/dashboard/grades")} />)}
        </div>
      ) : (
        <section className="complete-box"><div><strong>No certificates yet</strong><small>Enroll in a course and meet its requirements to earn one.</small></div></section>
      )}

      {viewing && (
        <div className="lightbox" role="dialog" aria-label="Certificate" onClick={(e) => e.target === e.currentTarget && setOpen(null)}>
          <div className="lightbox-in">
            <button className="back-btn" type="button" onClick={() => setOpen(null)}>✕ Close</button>
            <CertPreview name={name} course={viewing.course?.title} date={monthYear(viewing.issue_date)} number={viewing.certificate_number} />
            <div className="cta-row">
              <a className="cbtn go" href={`${BACKEND}/certificate/${viewing.uuid}/view`} target="_blank" rel="noopener noreferrer">Open the PDF</a>
              <small>The PDF has a working QR code that links to your verification page.</small>
            </div>
          </div>
        </div>
      )}
    </PageFrame>
  );
}
